import {UiHomeTileSizeEnum} from "../model/dto/enum/ui-home-tile-size.enum";

/**
 * The pixel arithmetic of a home tile.
 *
 * Why pixels at all: {@code widgetHeight: '100%'} is ACCEPTED by {@code MvsWidgetComponent}
 * and lands in the DOM, but it has no effect, because the parent chain carries no fixed height.
 * Measured in the spike: a 2x2 tile of 192px held a 3923px widget. A pixel value fits exactly
 * (400 -> 400, 192 -> 192), for {@code list} AND {@code table} alike. So: never {@code '100%'}.
 *
 * The 56px frame is {@code mvs-widget}'s own chrome around its scroller; it was measured
 * identically for {@code list} and {@code table}.
 */
export class UiHomeTileMetrics {

    /** Edge length of one grid cell. */
    static readonly CELL_PX = 88;

    /** Gap between two grid cells. */
    static readonly GAP_PX = 16;

    /**
     * What the tile chrome (head row plus padding) takes off the outer height before the
     * content area begins. The grid itself is built in a later block - pass
     * {@code contentHeightPx} into the widget tile to override this default with a measured value.
     */
    static readonly HEAD_PX = 40;

    /** Own frame of {@code mvs-widget} around its scroller - MEASURED, not estimated. */
    static readonly WIDGET_FRAME_PX = 56;

    /** Smallest widget height still worth rendering. */
    static readonly MIN_WIDGET_HEIGHT_PX = 40;

    private static readonly SPAN: { [key: number]: { cols: number; rows: number } } = {
        [UiHomeTileSizeEnum.s1x1]: {cols: 1, rows: 1},
        [UiHomeTileSizeEnum.s2x2]: {cols: 2, rows: 2},
        [UiHomeTileSizeEnum.s4x2]: {cols: 4, rows: 2},
        [UiHomeTileSizeEnum.s4x4]: {cols: 4, rows: 4},
    };

    /** Columns and rows a size spans in the grid. */
    static span(size: UiHomeTileSizeEnum): { cols: number; rows: number } {
        return UiHomeTileMetrics.SPAN[size] ?? UiHomeTileMetrics.SPAN[UiHomeTileSizeEnum.s2x2];
    }

    /** Outer height of the tile box: {@code rows * cell + (rows - 1) * gap}. */
    static outerHeightPx(size: UiHomeTileSizeEnum): number {
        const rows = UiHomeTileMetrics.span(size).rows;
        return rows * UiHomeTileMetrics.CELL_PX + (rows - 1) * UiHomeTileMetrics.GAP_PX;
    }

    /** Outer width of the tile box: {@code cols * cell + (cols - 1) * gap}. */
    static outerWidthPx(size: UiHomeTileSizeEnum): number {
        const cols = UiHomeTileMetrics.span(size).cols;
        return cols * UiHomeTileMetrics.CELL_PX + (cols - 1) * UiHomeTileMetrics.GAP_PX;
    }

    /** Height of the content area inside the tile, below the tile head. */
    static contentHeightPx(size: UiHomeTileSizeEnum): number {
        return UiHomeTileMetrics.outerHeightPx(size) - UiHomeTileMetrics.HEAD_PX;
    }

    /**
     * The value for the {@code widgetHeight} widget parameter: content height minus the 56px
     * frame of {@code mvs-widget}.
     *
     * @param size            the tile size
     * @param contentHeightPx measured content height, when the grid knows it better
     */
    static widgetHeightPx(size: UiHomeTileSizeEnum, contentHeightPx?: number): number {
        const content = contentHeightPx != null && contentHeightPx > 0
            ? contentHeightPx
            : UiHomeTileMetrics.contentHeightPx(size);

        return Math.max(UiHomeTileMetrics.MIN_WIDGET_HEIGHT_PX, content - UiHomeTileMetrics.WIDGET_FRAME_PX);
    }

    /** The {@code widgetHeight} parameter value, ready to hand to the widget - ALWAYS pixels. */
    static widgetHeight(size: UiHomeTileSizeEnum, contentHeightPx?: number): string {
        return `${UiHomeTileMetrics.widgetHeightPx(size, contentHeightPx)}px`;
    }
}
